import { useTranslation } from "react-i18next";
import { useCallback, useState } from "react";
import dayjs from "dayjs";
import { AppForm, FormDatePicker, FormInput } from "@/shared/Components";
import { FormButtonSelect } from "@/shared/Components/Form/FormButtonSelect";
import type { UseFormReturn } from "react-hook-form";
import type { Person } from "@/services/api";
import type { PersonInfoCardFormValues } from "@/entities/person-info-card/model";
import styles from "./styles.module.scss";

export const InfoEdit = ({
  currentPerson,
  form,
  onSubmit,
}: {
  currentPerson: Person;
  form: UseFormReturn<PersonInfoCardFormValues>;
  onSubmit: (data: PersonInfoCardFormValues) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const [isYearMode, setIsYearMode] = useState(!currentPerson?.birthdaySDate?.year);

  const handleBlur = useCallback(() => {
    form.handleSubmit(onSubmit)();
  }, [form, onSubmit]);

  const handleDatePickerChange = useCallback(() => {
    handleBlur();
    const value = form.getValues("birthday");
    setIsYearMode(!value || value.length === 5);
  }, [form, handleBlur]);

  return (
    <AppForm formApi={form} className={styles.root}>
      <FormInput
        name="firstName"
        placeholder={t("contactsPage.generalInfo.firstName")}
        onBlur={handleBlur}
      />
      <FormInput
        name="middleName"
        placeholder={t("contactsPage.generalInfo.middleName")}
        onBlur={handleBlur}
      />
      <FormInput
        name="lastName"
        placeholder={t("contactsPage.generalInfo.lastName")}
        onBlur={handleBlur}
      />
      <FormButtonSelect
        placeholder={t("common.gender")}
        name="gender"
        allowClear
        options={[
          { label: t("common.male"), value: "MALE" },
          { label: t("common.female"), value: "FEMALE" },
          { label: t("common.notDefined"), value: "NOT_DEFINED" },
        ]}
        onBlur={handleBlur}
      />
      <FormDatePicker
        isOnlyYearPicker={isYearMode}
        hideOnlyYearCheckbox={false}
        placeholder={t("settingsPage.general.birthday")}
        name="birthday"
        format={{
          type: "mask",
          format: isYearMode ? "DD-MM" : "DD-MM-YYYY",
        }}
        customOnChange={handleDatePickerChange}
        onOpenChange={(open) => {
          if (open) {
            const value = form.getValues("birthday");
            setIsYearMode(!value || value.length === 5);
          }
        }}
        renderExtraFooter={() => (
          <div className={styles["hide-year-checkbox-container"]}>
            <input
              type="checkbox"
              checked={isYearMode}
              onChange={(e) => {
                setIsYearMode(e.target.checked);
                const value = form.getValues("birthday");
                if (value) {
                  form.setValue("birthday", value, { shouldValidate: true });
                }
              }}
            />
            <span className={styles["hide-year-content"]}>{t("common.withoutYear")}</span>
          </div>
        )}
        disabledDate={(current) => {
          if (!current || isYearMode) return false;

          const today = dayjs().startOf("day");

          return current.isAfter(today);
        }}
      />
    </AppForm>
  );
};
